import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import { alpha, useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useGetMemberOrderByNoQuery } from '@onesource/services';
import { Link, useParams } from '@tanstack/react-router';
import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { HiArrowLeft, HiCreditCard } from 'react-icons/hi2';
import { CONFIG } from 'src/config-global';

import { PageContainer } from '#/components/page-container';

import { BillingOrderDetailPreloader } from './order-detail-preloader';

// ----------------------------------------------------------------------

const centsToUSD = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(iso));

const statusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (status.toLowerCase()) {
    case 'completed':
    case 'active': {
      return 'success';
    }
    case 'failed':
    case 'cancelled':
    case 'expired': {
      return 'error';
    }
    case 'pending': {
      return 'warning';
    }
    default: {
      return 'default';
    }
  }
};

// ----------------------------------------------------------------------

export const OrderDetailPage: FC = () => {
  const theme = useTheme();
  const { orderNo } = useParams({ from: '/orders/$orderNo' });
  const { data: order, isLoading, isError } = useGetMemberOrderByNoQuery(orderNo);

  if (isLoading) {
    return <BillingOrderDetailPreloader />;
  }

  if (isError || !order) {
    return (
      <Container maxWidth="xl" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 8 }}>
          <Typography variant="h5" color="text.secondary">
            Order not found
          </Typography>
          <Typography variant="body2" color="text.disabled">
            We couldn&apos;t find order #{orderNo}.
          </Typography>
          <Link to="/billing" search={{ tab: 'orders' }}>
            <Button variant="outlined" color="inherit" sx={{ mt: 1 }}>
              Back to Billing
            </Button>
          </Link>
        </Box>
      </Container>
    );
  }

  const tax = 0; // placeholder

  return (
    <>
      <Helmet>
        <title>{`Order #${order.orderNo} - ${CONFIG.appName}`}</title>
      </Helmet>

      <PageContainer>
        <Container maxWidth="xl" sx={{ px: 0, py: { xs: 2, sm: 4 } }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 4,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Link to="/billing" search={{ tab: 'orders' }} style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  color="inherit"
                  sx={{
                    width: { xs: 40, md: 48 },
                    height: { xs: 40, md: 48 },
                    borderRadius: '50%',
                    p: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.lighter',
                    color: 'primary.main',
                    boxShadow: 'none',
                    transition: 'background-color 0.2s, color 0.2s',
                    '&:hover': { bgcolor: 'primary.dark', color: '#fff', boxShadow: 'none' },
                  }}
                >
                  <HiArrowLeft size={22} />
                </Button>
              </Link>
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  Order #{order.orderNo}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Placed on {formatDate(order.date)}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              color={statusColor(order.status)}
              sx={{ fontWeight: 600, fontSize: 13, px: 1 }}
            />
          </Box>

          {/* Order items table */}
          <TableContainer
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              mb: 3,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
                  <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Qty
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Unit Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Discount
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.products.map(product => (
                  <TableRow key={product.productId} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {product.productName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{product.quantity}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{centsToUSD(product.price)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {centsToUSD(product.price * product.quantity)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totals summary */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box
              sx={{
                width: { xs: '100%', sm: 320 },
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="body2">{centsToUSD(order.subtotal)}</Typography>
              </Box>
              {order.discount > 0 && (
                <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Discount
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    −{centsToUSD(order.discount)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Tax
                </Typography>
                <Typography variant="body2">{tax > 0 ? centsToUSD(tax) : '—'}</Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  bgcolor: alpha(theme.palette.grey[500], 0.04),
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Total
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {centsToUSD(order.total)}
                  </Typography>
                  {order.paymentUrl && (
                    <Button
                      component="a"
                      href={order.paymentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      size="small"
                      color="primary"
                      endIcon={<HiCreditCard size={16} />}
                      sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        '& .MuiButton-endIcon': { marginRight: 0 },
                      }}
                    >
                      Pay
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </PageContainer>
    </>
  );
};
